import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { FiAlertCircle, FiInbox, FiLoader, FiSearch, FiX } from "react-icons/fi";
import "./Farms.css";
import * as SM from "./Farms.mobile.styled";
import * as D from "./Farms.desktop.styled";
import { getFarms, getSavedFarmIds, toggleWishlist } from "../../Utils/storeService";
import { useAuth } from "../../context/AuthContext";
import { useAuthPrompt } from "../../context/AuthPromptContext";
import { useToast } from "../../context/ToastContext";
import { usePullToRefresh } from "../../hooks/usePullToRefresh";
import { useDebounce } from "../../hooks/useDebounce";
import { useIsDesktop } from "../../hooks/useMediaQuery";
import PullIndicator from "../../ui/PullIndicator";
import {
  createDefaultFilters,
  cloneFilters,
  mapApiFarmToCatalog,
  computeFilteredFarms,
  REGION_OPTION_DEFS,
  PRODUCT_TYPE_OPTION_DEFS,
  DELIVERY_WINDOW_OPTION_DEFS,
  SORT_OPTION_DEFS,
} from "./Farms.constants";
import FarmCardList from "./FarmCardList";
import FarmFilterSheet from "./FarmFilterSheet";
import FarmsHeroDesktop from "./FarmsHeroDesktop";
import FarmsCommandBar from "./FarmsCommandBar";
import FarmCardDesktop from "./FarmCardDesktop";
import FarmCardDesktopSkeleton from "./FarmCardDesktopSkeleton";
import FarmFeaturesDesktop from "./FarmFeaturesDesktop";
import FarmNewsletterDesktop from "./FarmNewsletterDesktop";
import FarmModalDesktop from "./FarmModalDesktop";
import FarmFilterDrawerDesktop from "./FarmFilterDrawerDesktop";

const FARMS_SCREEN_MEMORY_KEY = "dora_farms_screen_memory";
const FARMS_GRID_ANCHOR_ID = "farms-grid";

const getDefaultScreenMemory = () => ({
  searchQuery: "",
  viewMode: "grid",
  sortOption: "recommended",
  filters: createDefaultFilters(),
  pendingFilters: createDefaultFilters(),
  farmsCatalog: [],
  catalogError: "",
  visibleCount: 0,
  hasLoadedCatalog: false,
  scrollY: 0,
});

const readFarmsScreenMemory = () => {
  const defaults = getDefaultScreenMemory();
  if (typeof window === "undefined") {
    return defaults;
  }

  try {
    const raw = window.sessionStorage.getItem(FARMS_SCREEN_MEMORY_KEY);
    if (!raw) {
      return defaults;
    }

    const parsed = JSON.parse(raw);
    const filters = {
      ...defaults.filters,
      ...(parsed.filters || {}),
    };

    return {
      ...defaults,
      ...parsed,
      filters,
      pendingFilters: {
        ...defaults.pendingFilters,
        ...(parsed.pendingFilters || filters),
      },
      farmsCatalog: Array.isArray(parsed.farmsCatalog)
        ? parsed.farmsCatalog
        : defaults.farmsCatalog,
      visibleCount:
        typeof parsed.visibleCount === "number"
          ? parsed.visibleCount
          : defaults.visibleCount,
      hasLoadedCatalog: Boolean(parsed.hasLoadedCatalog),
      scrollY: typeof parsed.scrollY === "number" ? parsed.scrollY : 0,
    };
  } catch {
    return defaults;
  }
};

const writeFarmsScreenMemory = (snapshot) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      FARMS_SCREEN_MEMORY_KEY,
      JSON.stringify(snapshot)
    );
  } catch {
    // Ignore storage quota or privacy-mode failures.
  }
};

const Farms = () => {
  const initialMemoryRef = useRef(readFarmsScreenMemory());
  const initialMemory = initialMemoryRef.current;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { token } = useAuth();
  const { openAuthPrompt } = useAuthPrompt();
  const { success: toastSuccess, error: toastError } = useToast();
  const [savedFarmIds, setSavedFarmIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState(initialMemory.searchQuery);
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [viewMode, setViewMode] = useState(initialMemory.viewMode);
  const [sortOption, setSortOption] = useState(initialMemory.sortOption);
  const [filters, setFilters] = useState(initialMemory.filters);
  const [pendingFilters, setPendingFilters] = useState(initialMemory.pendingFilters);
  const [farmsCatalog, setFarmsCatalog] = useState(initialMemory.farmsCatalog);
  const [catalogError, setCatalogError] = useState(initialMemory.catalogError);
  const [isFilterOpen, setFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(!initialMemory.hasLoadedCatalog);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMounted, setHasMounted] = useState(initialMemory.hasLoadedCatalog);
  const [visibleCount, setVisibleCount] = useState(() =>
    Math.max(
      0,
      Math.min(initialMemory.visibleCount, initialMemory.farmsCatalog.length)
    )
  );
  const loadMoreTimerRef = useRef(null);
  const skipInitialLoadingAnimationRef = useRef(initialMemory.hasLoadedCatalog);
  const skipInitialVisibleResetRef = useRef(initialMemory.hasLoadedCatalog);
  const pendingScrollRestoreRef = useRef(
    initialMemory.hasLoadedCatalog && initialMemory.scrollY > 0
  );

  const toOption = (def) => ({ value: def.value, label: t(def.key, { defaultValue: def.defaultValue }) });
  const regionOptions = useMemo(() => REGION_OPTION_DEFS.map(toOption), [t]); // eslint-disable-line react-hooks/exhaustive-deps
  const productTypeOptions = useMemo(() => PRODUCT_TYPE_OPTION_DEFS.map(toOption), [t]); // eslint-disable-line react-hooks/exhaustive-deps
  const deliveryWindowOptions = useMemo(() => DELIVERY_WINDOW_OPTION_DEFS.map(toOption), [t]); // eslint-disable-line react-hooks/exhaustive-deps
  const sortOptions = useMemo(() => SORT_OPTION_DEFS.map(toOption), [t]); // eslint-disable-line react-hooks/exhaustive-deps

  const deliveryLabelMap = useMemo(
    () => deliveryWindowOptions.reduce((acc, o) => { acc[o.value] = o.label; return acc; }, {}),
    [deliveryWindowOptions]
  );

  const regionLabelMap = useMemo(
    () => regionOptions.reduce((acc, o) => { acc[o.value] = o.label; return acc; }, {}),
    [regionOptions]
  );

  const productTypeLabelMap = useMemo(
    () => productTypeOptions.reduce((acc, o) => { acc[o.value] = o.label; return acc; }, {}),
    [productTypeOptions]
  );

  const ctaLabels = useMemo(() => ({
    viewFarm: t("farms.card_button", { defaultValue: "View Farm" }),
    shop: t("farms.cta.shop", { defaultValue: "Shop" }),
    contact: t("farms.cta.contact", { defaultValue: "Contact" }),
  }), [t]);

  const filteredFarms = useMemo(
    () => computeFilteredFarms(farmsCatalog, filters, debouncedSearch, sortOption),
    [farmsCatalog, filters, debouncedSearch, sortOption]
  );

  const displayedFarms = useMemo(
    () => filteredFarms.slice(0, visibleCount),
    [filteredFarms, visibleCount]
  );

  const canLoadMore = visibleCount < filteredFarms.length;

  const heroStats = useMemo(() => {
    const regions = new Set(farmsCatalog.map((farm) => farm.region).filter(Boolean));
    return {
      farmCount: farmsCatalog.length,
      regionCount: regions.size,
      organicCount: farmsCatalog.filter((farm) => farm.isOrganic).length,
    };
  }, [farmsCatalog]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (filters.region && filters.region !== "all") {
      chips.push({
        id: `region:${filters.region}`,
        kind: "region",
        value: filters.region,
        label: regionLabelMap[filters.region] ?? filters.region,
      });
    }
    filters.productTypes.forEach((value) => {
      chips.push({
        id: `product:${value}`,
        kind: "product",
        value,
        label: productTypeLabelMap[value] ?? value,
      });
    });
    if (filters.organicOnly) {
      chips.push({
        id: "organic:true",
        kind: "organic",
        value: true,
        label: t("farms.filters.organic.label", { defaultValue: "Organic" }),
      });
    }
    if (filters.deliveryWindow && filters.deliveryWindow !== "any") {
      chips.push({
        id: `delivery:${filters.deliveryWindow}`,
        kind: "delivery",
        value: filters.deliveryWindow,
        label: deliveryLabelMap[filters.deliveryWindow] ?? filters.deliveryWindow,
      });
    }
    return chips;
  }, [filters, regionLabelMap, productTypeLabelMap, deliveryLabelMap, t]);

  const activeFilterCount = activeFilterChips.length;

  const buildScreenMemorySnapshot = useCallback(
    (scrollY = typeof window !== "undefined" ? window.scrollY : 0) => ({
      searchQuery,
      viewMode,
      sortOption,
      filters,
      pendingFilters: filters,
      farmsCatalog,
      catalogError,
      visibleCount:
        farmsCatalog.length > 0
          ? Math.min(visibleCount, farmsCatalog.length)
          : visibleCount,
      hasLoadedCatalog: hasMounted,
      scrollY,
    }),
    [
      searchQuery,
      viewMode,
      sortOption,
      filters,
      farmsCatalog,
      catalogError,
      visibleCount,
      hasMounted,
    ]
  );

  const persistScreenMemory = useCallback(
    (scrollY) => {
      writeFarmsScreenMemory(buildScreenMemorySnapshot(scrollY));
    },
    [buildScreenMemorySnapshot]
  );

  const fetchFarms = useCallback(async () => {
    try {
      const data = await getFarms();
      if (Array.isArray(data) && data.length > 0) {
        const nextCatalog = data.map(mapApiFarmToCatalog);
        setFarmsCatalog(nextCatalog);
        setVisibleCount((prev) =>
          prev > 0 ? Math.min(prev, nextCatalog.length) : Math.min(6, nextCatalog.length)
        );
        setCatalogError("");
      } else {
        setFarmsCatalog([]);
        setVisibleCount(0);
        setCatalogError("");
      }
    } catch (err) {
      console.error("Failed to load farms.", err);
      setFarmsCatalog([]);
      setVisibleCount(0);
      setCatalogError(t("farms.load_error", { defaultValue: "Live farms are unavailable right now. Please try again." }));
    } finally {
      setIsLoading(false);
      setHasMounted(true);
    }
  }, [t]);

  useEffect(() => {
    if (initialMemory.hasLoadedCatalog) {
      return;
    }
    fetchFarms();
  }, [fetchFarms, initialMemory.hasLoadedCatalog]);

  const { refreshing, pullDistance } = usePullToRefresh(fetchFarms);

  useEffect(() => {
    if (!token) { setSavedFarmIds(new Set()); return; }
    let active = true;
    getSavedFarmIds(token).then((ids) => { if (active && Array.isArray(ids)) setSavedFarmIds(new Set(ids)); }).catch(() => {
      toastError(t("farms.wishlist_load_error", { defaultValue: "Could not load your saved farms." }));
    });
    return () => { active = false; };
  }, [token, toastError, t]);

  useEffect(() => {
    persistScreenMemory();
  }, [persistScreenMemory]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    let frame = null;
    const handleScroll = () => {
      if (frame !== null) {
        return;
      }

      frame = window.requestAnimationFrame(() => {
        persistScreenMemory(window.scrollY);
        frame = null;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
      persistScreenMemory(window.scrollY);
    };
  }, [persistScreenMemory]);

  const performWishlistToggle = async (farmId) => {
    const wasSaved = savedFarmIds.has(farmId);
    const toggle = (prev) => { const s = new Set(prev); wasSaved ? s.delete(farmId) : s.add(farmId); return s; };
    const revert = (prev) => { const s = new Set(prev); wasSaved ? s.add(farmId) : s.delete(farmId); return s; };
    setSavedFarmIds(toggle);
    try { await toggleWishlist(token, farmId); } catch {
      setSavedFarmIds(revert);
      toastError(t("farms.wishlist_error", { defaultValue: "Could not update wishlist." }));
    }
  };

  const handleToggleWishlist = (event, farmId) => {
    event.stopPropagation();
    if (!token) {
      openAuthPrompt({
        title: t("auth_prompt.wishlist_title", { defaultValue: "Log in to save farms" }),
        body: t("auth_prompt.wishlist_body", { defaultValue: "Create an account or log in to save your favorite farms." }),
        source: "wishlist",
        onSuccess: () => performWishlistToggle(farmId),
      });
      return;
    }
    performWishlistToggle(farmId);
  };

  useEffect(() => {
    if (!hasMounted) return;
    if (skipInitialLoadingAnimationRef.current) {
      skipInitialLoadingAnimationRef.current = false;
      return;
    }
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 320);
    return () => clearTimeout(timer);
  }, [filters, sortOption, searchQuery, hasMounted]);

  useEffect(() => {
    if (!hasMounted) return;
    if (skipInitialVisibleResetRef.current) {
      skipInitialVisibleResetRef.current = false;
      return;
    }
    setVisibleCount(() => { const b = Math.min(6, filteredFarms.length || 0); return b > 0 ? b : filteredFarms.length; });
  }, [filters, sortOption, searchQuery, filteredFarms.length, hasMounted]);

  useEffect(() => {
    if (!isFilterOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isFilterOpen]);

  useEffect(() => () => { if (loadMoreTimerRef.current) clearTimeout(loadMoreTimerRef.current); }, []);

  useEffect(() => {
    if (!pendingScrollRestoreRef.current || isLoading) {
      return;
    }

    pendingScrollRestoreRef.current = false;
    const targetScrollY = initialMemory.scrollY;
    const restore = window.requestAnimationFrame(() => {
      if (typeof window.scrollTo !== "function") {
        return;
      }

      try {
        window.scrollTo(0, targetScrollY);
      } catch {
        // Ignore non-browser environments that stub or reject scroll APIs.
      }
    });

    return () => window.cancelAnimationFrame(restore);
  }, [initialMemory.scrollY, isLoading, displayedFarms.length]);

  const updatePendingFilters = (updater) => setPendingFilters((prev) => updater(cloneFilters(prev)));
  const handleOpenFilters = () => { setPendingFilters(cloneFilters(filters)); setFilterOpen(true); };
  const handleApplyFilters = () => { setFilters(cloneFilters(pendingFilters)); setFilterOpen(false); };
  const handleResetFilters = () => { const d = createDefaultFilters(); setFilters(d); setPendingFilters(d); };
  const handleClearPendingFilters = () => setPendingFilters(createDefaultFilters());
  const handleToggleProductType = (value) => updatePendingFilters((draft) => {
    draft.productTypes = draft.productTypes.includes(value)
      ? draft.productTypes.filter((item) => item !== value)
      : [...draft.productTypes, value];
    return draft;
  });
  const handleRegionSelect = (value) => updatePendingFilters((draft) => { draft.region = value; return draft; });
  const handleDeliverySelect = (value) => updatePendingFilters((draft) => { draft.deliveryWindow = value; return draft; });
  const handleOrganicToggle = (checked) => updatePendingFilters((draft) => { draft.organicOnly = checked; return draft; });

  const handleQuickRegionSelect = (value) => {
    setFilters((prev) => ({ ...cloneFilters(prev), region: value }));
  };
  const handleQuickOrganicToggle = (checked) => {
    setFilters((prev) => ({ ...cloneFilters(prev), organicOnly: checked }));
  };

  const handleRemoveChip = (chip) => {
    setFilters((prev) => {
      const next = cloneFilters(prev);
      if (chip.kind === "region") next.region = "all";
      else if (chip.kind === "product") {
        next.productTypes = next.productTypes.filter((v) => v !== chip.value);
      } else if (chip.kind === "organic") next.organicOnly = false;
      else if (chip.kind === "delivery") next.deliveryWindow = "any";
      return next;
    });
  };

  const handleExploreScroll = () => {
    if (typeof document === "undefined") return;
    const target = document.getElementById(FARMS_GRID_ANCHOR_ID);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleLoadMore = () => {
    if (!canLoadMore || isLoadingMore) return;
    setIsLoadingMore(true);
    if (loadMoreTimerRef.current) clearTimeout(loadMoreTimerRef.current);
    loadMoreTimerRef.current = setTimeout(() => {
      setVisibleCount((prev) => Math.min(prev + 6, filteredFarms.length));
      setIsLoadingMore(false);
      loadMoreTimerRef.current = null;
    }, 420);
  };

  const handleContact = (farmId) => {
    navigate(`/contact?farm=${encodeURIComponent(farmId)}`);
  };

  const openModal = (farm) => setSelectedFarm(farm);
  const closeModal = () => setSelectedFarm(null);

  const desktopSkeletonCount = 6;

  return (
    <SM.Page>
      <PullIndicator pullDistance={pullDistance} refreshing={refreshing} />

      {isDesktop ? (
      <D.DesktopRoot>
        <FarmsHeroDesktop stats={heroStats} onExplore={handleExploreScroll} />

        <D.Container>
          <FarmsCommandBar
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            sortOption={sortOption}
            onSortChange={setSortOption}
            sortOptions={sortOptions}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            filters={filters}
            onRegionSelect={handleQuickRegionSelect}
            onOrganicToggle={handleQuickOrganicToggle}
            regionOptions={regionOptions}
            activeFilterChips={activeFilterChips}
            activeFilterCount={activeFilterCount}
            onOpenFilters={handleOpenFilters}
            onRemoveChip={handleRemoveChip}
            onResetFilters={handleResetFilters}
          />

          {catalogError ? (
            <D.ErrorBanner role="alert">
              <FiAlertCircle aria-hidden="true" size={20} />
              {catalogError}
            </D.ErrorBanner>
          ) : null}

          <D.ResultsHeader id={FARMS_GRID_ANCHOR_ID}>
            <D.SectionTitleGroup>
              <D.SectionKicker>
                {t("farms.section_kicker", { defaultValue: "The marketplace" })}
              </D.SectionKicker>
              <D.SectionTitle>
                {t("farms.section_title", { defaultValue: "Discover Our Farms" })}
              </D.SectionTitle>
            </D.SectionTitleGroup>
            {!isLoading ? (
              <D.ResultsMeta>
                <strong>{filteredFarms.length}</strong>{" "}
                {t("farms.results.count", {
                  defaultValue: "farms found",
                  count: filteredFarms.length,
                })}
              </D.ResultsMeta>
            ) : null}
          </D.ResultsHeader>

          {isLoading ? (
            <D.Grid aria-busy="true" aria-live="polite">
              {Array.from({ length: desktopSkeletonCount }).map((_, i) => (
                <FarmCardDesktopSkeleton key={`desktop-skeleton-${i}`} />
              ))}
            </D.Grid>
          ) : filteredFarms.length === 0 ? (
            <D.EmptyState>
              <D.EmptyIcon>
                <FiInbox aria-hidden="true" />
              </D.EmptyIcon>
              <D.EmptyTitle>
                {t("farms.empty.title", {
                  defaultValue: "No farms match these filters",
                })}
              </D.EmptyTitle>
              <D.EmptyCopy>
                {t("farms.empty.copy", {
                  defaultValue:
                    "Try adjusting the filters or broadening your delivery window.",
                })}
              </D.EmptyCopy>
              <div>
                <D.LoadMoreButton type="button" onClick={handleResetFilters}>
                  {t("farms.empty.clear", { defaultValue: "Clear filters" })}
                </D.LoadMoreButton>
              </div>
            </D.EmptyState>
          ) : (
            <>
              <AnimatePresence mode="popLayout">
                {viewMode === "grid" ? (
                  <D.Grid
                    key="grid"
                    as={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {displayedFarms.map((farm, index) => (
                      <FarmCardDesktop
                        key={farm.id}
                        farm={farm}
                        variant="grid"
                        index={index}
                        isSaved={savedFarmIds.has(farm.id)}
                        onToggleWishlist={handleToggleWishlist}
                        onOpenModal={openModal}
                        onViewFarm={(id) => navigate(`/farms/${id}`)}
                        onContact={handleContact}
                        deliveryLabel={
                          deliveryLabelMap[farm.deliveryWindow] ??
                          deliveryWindowOptions[0].label
                        }
                        ctaLabels={ctaLabels}
                      />
                    ))}
                    {isLoadingMore
                      ? Array.from({
                          length: Math.max(
                            1,
                            Math.min(3, filteredFarms.length - displayedFarms.length)
                          ),
                        }).map((_, index) => (
                          <FarmCardDesktopSkeleton
                            key={`desktop-loadmore-${index}`}
                          />
                        ))
                      : null}
                  </D.Grid>
                ) : (
                  <D.ListStack
                    key="list"
                    as={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {displayedFarms.map((farm, index) => (
                      <FarmCardDesktop
                        key={farm.id}
                        farm={farm}
                        variant="list"
                        index={index}
                        isSaved={savedFarmIds.has(farm.id)}
                        onToggleWishlist={handleToggleWishlist}
                        onOpenModal={openModal}
                        onViewFarm={(id) => navigate(`/farms/${id}`)}
                        onContact={handleContact}
                        deliveryLabel={
                          deliveryLabelMap[farm.deliveryWindow] ??
                          deliveryWindowOptions[0].label
                        }
                        ctaLabels={ctaLabels}
                      />
                    ))}
                  </D.ListStack>
                )}
              </AnimatePresence>

              {canLoadMore ? (
                <D.LoadMoreWrap>
                  <D.LoadMoreButton
                    type="button"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? (
                      <>
                        <FiLoader aria-hidden="true" />
                        {t("farms.loading_more", {
                          defaultValue: "Loading farms…",
                        })}
                      </>
                    ) : (
                      t("farms.load_more", { defaultValue: "Load more farms" })
                    )}
                  </D.LoadMoreButton>
                </D.LoadMoreWrap>
              ) : null}
            </>
          )}
        </D.Container>

        <FarmFeaturesDesktop />

        <FarmNewsletterDesktop onSuccess={toastSuccess} onError={toastError} />

        <FarmModalDesktop
          farm={selectedFarm}
          onClose={closeModal}
          isSaved={selectedFarm ? savedFarmIds.has(selectedFarm.id) : false}
          onToggleWishlist={handleToggleWishlist}
          deliveryLabel={
            selectedFarm
              ? deliveryLabelMap[selectedFarm.deliveryWindow] ??
                deliveryWindowOptions[0].label
              : ""
          }
        />

        <FarmFilterDrawerDesktop
          open={isFilterOpen}
          pendingFilters={pendingFilters}
          onRegionSelect={handleRegionSelect}
          onToggleProductType={handleToggleProductType}
          onOrganicToggle={handleOrganicToggle}
          onDeliverySelect={handleDeliverySelect}
          onApply={handleApplyFilters}
          onClearPending={handleClearPendingFilters}
          onClose={() => setFilterOpen(false)}
          regionOptions={regionOptions}
          productTypeOptions={productTypeOptions}
          deliveryWindowOptions={deliveryWindowOptions}
        />
      </D.DesktopRoot>
      ) : null}

      {!isDesktop ? (
      <SM.MobileContainer style={{ display: "flex" }}>
        <SM.StickyHeader>
          <SM.HeaderRow>
            <SM.HeaderTitle>{t("farms.section_title_mobile", { defaultValue: "Farms near you" })}</SM.HeaderTitle>
          </SM.HeaderRow>
          <SM.SearchRow>
            <SM.SearchField>
              <FiSearch aria-hidden="true" />
              <SM.SearchInput
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("farms.search.placeholder", { defaultValue: "Search farms..." })}
                aria-label={t("farms.search.label", { defaultValue: "Search farms" })}
              />
              {searchQuery ? (
                <SM.SearchClear
                  type="button"
                  onClick={() => setSearchQuery("")}
                  aria-label={t("farms.search.clear", { defaultValue: "Clear search" })}
                >
                  <FiX />
                </SM.SearchClear>
              ) : null}
            </SM.SearchField>
          </SM.SearchRow>
          <SM.HeaderActions>
            <SM.FilterButton
              type="button"
              variant="primary"
              fullWidth
              onClick={handleOpenFilters}
            >
              {t("farms.filters.open", { defaultValue: "Filters" })}
              {activeFilterCount > 0 ? (
                <SM.FilterBadge>{activeFilterCount}</SM.FilterBadge>
              ) : null}
            </SM.FilterButton>
            <SM.SortSelect
              value={sortOption}
              onChange={(event) => setSortOption(event.target.value)}
              aria-label={t("farms.sort.label", { defaultValue: "Sort farms" })}
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SM.SortSelect>
          </SM.HeaderActions>
        </SM.StickyHeader>

        <SM.Content>
          {catalogError ? <SM.EmptyState>{catalogError}</SM.EmptyState> : null}
          <FarmCardList
            isLoading={isLoading}
            filteredFarms={filteredFarms}
            displayedFarms={displayedFarms}
            savedFarmIds={savedFarmIds}
            onToggleWishlist={handleToggleWishlist}
            onResetFilters={handleResetFilters}
            canLoadMore={canLoadMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={handleLoadMore}
            deliveryLabelMap={deliveryLabelMap}
            deliveryWindowOptions={deliveryWindowOptions}
            ctaLabels={ctaLabels}
          />
        </SM.Content>

        {isFilterOpen && (
          <FarmFilterSheet
            pendingFilters={pendingFilters}
            onRegionSelect={handleRegionSelect}
            onToggleProductType={handleToggleProductType}
            onOrganicToggle={handleOrganicToggle}
            onDeliverySelect={handleDeliverySelect}
            onApply={handleApplyFilters}
            onClearPending={handleClearPendingFilters}
            onClose={() => setFilterOpen(false)}
            regionOptions={regionOptions}
            productTypeOptions={productTypeOptions}
            deliveryWindowOptions={deliveryWindowOptions}
          />
        )}
      </SM.MobileContainer>
      ) : null}
    </SM.Page>
  );
};

export default Farms;
